import { addDays, getISOWeek } from 'date-fns';
import React, { useRef } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Flatpickr from 'react-flatpickr';
import {
  cloneDate,
  isSameOrAfter,
  isSameOrBefore,
} from '../../utils/dateUtils';
import { toFlatpickrLocale } from '../form/flatpickrUtils';
import { useCurrentLocale } from '../../context/I18n';
import Button from '../button';

import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

type FlatpickrRef = InstanceType<typeof Flatpickr> & {
  flatpickr: FlatpickrInstance;
};

type Props = {
  minWeek?: Date;
  maxWeek?: Date;
  currentWeek: Date;
  onWeekChanged: (newValue: Date) => void;
};
const WeekNav: React.FC<Props> = ({
  currentWeek,
  minWeek,
  maxWeek,
  onWeekChanged,
}) => {
  const { t } = useTranslation();
  const locale = toFlatpickrLocale(useCurrentLocale());
  const flatpickrRef = useRef<FlatpickrRef | null>(null);

  const handleDatePickerClick = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open();
    }
  };

  const handleDateChange = (dates: Date[]) => {
    if (dates[0] && onWeekChanged) {
      onWeekChanged(dates[0]);
    }
  };

  return (
    <div>
      <ButtonGroup size="sm" className="bg-gray rounded p-1">
        <Button
          disabled={
            minWeek && isSameOrBefore(currentWeek, minWeek, { unit: 'day' })
          }
          label="←"
          variant="gray"
          onClick={() =>
            onWeekChanged && onWeekChanged(addDays(cloneDate(currentWeek), -7))
          }
        />
        <div className="week-nav-datepicker-container">
          <Button
            variant="white"
            size="sm"
            className="rounded"
            label={t('calendarWeek', { week: getISOWeek(currentWeek) })}
            onClick={handleDatePickerClick}
          />
          <Flatpickr
            ref={flatpickrRef}
            className="datepicker-input-hidden"
            options={{
              locale,
              dateFormat: t('flatpickr.dateFormat'),
              enableTime: false,
              clickOpens: false,
              minDate: minWeek,
              maxDate: maxWeek,
            }}
            value={currentWeek}
            onChange={handleDateChange}
          />
        </div>
        <Button
          disabled={
            maxWeek && isSameOrAfter(currentWeek, maxWeek, { unit: 'day' })
          }
          label="→"
          variant="gray"
          onClick={() =>
            onWeekChanged && onWeekChanged(addDays(cloneDate(currentWeek), 7))
          }></Button>
      </ButtonGroup>
    </div>
  );
};

export default WeekNav;
